import React from 'react';
import { ArrowRight, Check, Quote } from 'lucide-react';

/**
 * Preuve sociale + tarif — planche 5 de la maquette « Refonte conversion ».
 *
 * Contraintes portées par l'annotation de la planche, à ne pas défaire :
 *  - témoignages repris MOT POUR MOT, avec prénom, commune et type de dette ;
 *  - pas d'étoiles inventées : un guillemet typographique à la place, faute de notes ;
 *  - chiffres réels issus de l'admin (154 / 73 % / 3 min).
 *
 * Deux points restaient à confirmer avant mise en ligne : l'accord des trois
 * personnes citées, et le caractère ferme et général du −50 % (une remise variable
 * selon le cabinet ne peut pas s'afficher comme un prix).
 */

const TEMOIGNAGES = [
  { initiale: 'É', texte: "Merci de m'avoir aidé, je ne savais plus comment faire.", nom: 'Éric', meta: 'Ivry-sur-Seine · Crédit consommation' },
  { initiale: 'K', texte: "Après le courrier de demande de pièces, je n'ai plus jamais eu de nouvelles d'eux [les huissiers].", nom: 'Karine', meta: 'Concarneau · Saisie attribution' },
  { initiale: 'S', texte: "Le tarif partenaire de −50 % auprès d'un avocat est vraiment intéressant !", nom: 'Simon-Pierre', meta: 'Toulon · Saisie attribution' },
];

const STATS = [
  { valeur: '154', libelle: 'dossiers analysés' },
  { valeur: '73 %', libelle: 'présentaient au moins une irrégularité' },
  { valeur: '3 min', libelle: "temps moyen d'analyse" },
];

const OFFRES = [
  {
    badge: null, prix: '0 €', titre: 'Diagnostic complet',
    lignes: [
      'Votre score de contestabilité',
      'Le résumé de votre situation en français clair',
      'Les points à surveiller et les zones floues',
      'La liste des documents à retrouver',
    ],
    cta: 'Analyser mes documents — gratuit', pied: null, accent: true,
  },
  {
    badge: 'Optionnel', prix: '9,99 €', titre: "Plan d'action",
    lignes: [
      'Tout le diagnostic gratuit, plus :',
      'Vos courriers rédigés, prêts à poster',
      "L'ordre des démarches et les délais à tenir",
      'Les erreurs qui font repartir la prescription à zéro',
    ],
    cta: null, pied: 'Proposé à la fin de votre diagnostic gratuit', accent: false,
  },
  {
    badge: 'Sur demande', prix: '−50 %', titre: 'Mise en relation',
    lignes: [
      'Un avocat partenaire inscrit au barreau, intervenant en droit de la consommation et du recouvrement',
      'Tarif partenaire négocié : −50 % sur les honoraires de consultation',
      'La mise en relation elle-même ne vous coûte rien',
      'Vous décidez de donner suite ou non — aucun engagement',
    ],
    cta: null, pied: 'En cochant la case au dépôt de votre dossier', accent: false,
  },
];

const PreuveTarif = ({ onCTAClick }) => (
  <>
    {/* Bande A — preuve */}
    <section className="bg-white px-[18px] py-12 sm:px-6 sm:py-16 lg:px-[72px]">
      <div className="mx-auto max-w-[1200px]">
        <div className="mx-auto mb-8 max-w-[680px] text-center sm:mb-10">
          <h2 className="m-0 text-[26px] font-extrabold leading-tight tracking-tight sm:text-[34px]" style={{ color: '#1e3a8a' }}>
            Ils ont vérifié avant de payer
          </h2>
          <p className="mx-auto mt-3 max-w-[620px] text-[15px] leading-relaxed sm:text-[17px]" style={{ color: '#475569', textWrap: 'pretty' }}>
            Un courrier de recouvrement peut contenir des irrégularités de forme comme de fond.
            Encore faut-il savoir lesquelles.
          </p>
        </div>

        <div className="grid gap-4 sm:gap-5 lg:grid-cols-3">
          {TEMOIGNAGES.map((t) => (
            <div
              key={t.nom}
              className="flex flex-col gap-4 rounded-2xl border p-5 sm:p-6"
              style={{ borderColor: '#e2e8f0', backgroundColor: '#f8fafc' }}
            >
              <Quote size={22} style={{ color: '#c7d2fe' }} />
              <div className="flex-grow text-[14.5px] leading-relaxed sm:text-[15.5px]" style={{ color: '#1e293b' }}>
                {t.texte}
              </div>
              <div className="flex items-center gap-3">
                <div
                  className="flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-[15px] font-bold"
                  style={{ backgroundColor: '#e0e7ff', color: '#3730a3' }}
                >
                  {t.initiale}
                </div>
                <div className="flex flex-col">
                  <div className="text-sm font-semibold" style={{ color: '#1e293b' }}>{t.nom}</div>
                  <div className="text-[12.5px]" style={{ color: '#64748b' }}>{t.meta}</div>
                </div>
              </div>
            </div>
          ))}
        </div>

        <div
          className="mt-8 flex flex-wrap justify-around gap-6 rounded-2xl border px-6 py-6 sm:mt-10 sm:gap-8"
          style={{ borderColor: '#e2e8f0', backgroundColor: '#f1f5f9' }}
        >
          {STATS.map((s) => (
            <div key={s.valeur} className="flex flex-col items-center gap-1 text-center">
              <div className="text-[28px] font-extrabold leading-none sm:text-[32px]" style={{ color: '#1e3a8a' }}>
                {s.valeur}
              </div>
              <div className="max-w-[200px] text-[12.5px] sm:text-[13.5px]" style={{ color: '#64748b' }}>{s.libelle}</div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* Bande B — tarif */}
    <section className="px-[18px] py-12 sm:px-6 sm:py-16 lg:px-[72px]" style={{ backgroundColor: '#f8fafc' }}>
      <div className="mx-auto max-w-[1200px]">
        <div className="mx-auto mb-8 max-w-[680px] text-center sm:mb-10">
          <h2 className="m-0 text-[26px] font-extrabold leading-tight tracking-tight sm:text-[34px]" style={{ color: '#1e3a8a' }}>
            L'analyse est gratuite. Vraiment.
          </h2>
          <p className="mx-auto mt-3 text-[15px] leading-relaxed sm:text-[17px]" style={{ color: '#475569', textWrap: 'pretty' }}>
            Pas de carte bancaire, pas d'abonnement, pas de rappel commercial déguisé. Vous ne payez
            que si vous voulez le plan d'action détaillé.
          </p>
        </div>

        {/* Les trois cartouches se lisent de gauche à droite : gratuit, payant, sur demande. */}
        <div className="grid items-stretch gap-4 sm:gap-5 lg:grid-cols-3">
          {OFFRES.map((o) => (
            <div
              key={o.titre}
              className="relative flex flex-col gap-5 rounded-2xl border bg-white p-6"
              style={{
                borderColor: o.accent ? '#1e3a8a' : '#e2e8f0',
                boxShadow: o.accent ? '0 12px 24px -12px rgba(30, 58, 138, 0.25)' : 'none',
              }}
            >
              {o.badge && (
                <div
                  className="absolute right-5 top-5 rounded-full px-[10px] py-1 text-[11px] font-semibold uppercase tracking-wide"
                  style={{ backgroundColor: '#f1f5f9', color: '#64748b' }}
                >
                  {o.badge}
                </div>
              )}
              <div className="flex flex-col gap-1">
                <div className="text-[34px] font-extrabold leading-none" style={{ color: '#1e3a8a' }}>{o.prix}</div>
                <div className="text-[15px] font-semibold" style={{ color: '#1e293b' }}>{o.titre}</div>
              </div>
              <div className="flex flex-grow flex-col gap-[10px]">
                {o.lignes.map((l) => (
                  <div key={l} className="flex gap-[10px]">
                    <Check size={16} className="mt-[2px] shrink-0" style={{ color: '#16a34a' }} />
                    <span className="text-[13.5px] leading-snug" style={{ color: '#475569' }}>{l}</span>
                  </div>
                ))}
              </div>
              {o.cta ? (
                <button
                  type="button"
                  onClick={onCTAClick}
                  className="inline-flex items-center justify-center gap-2 rounded-full px-5 py-[13px] text-[14.5px] font-semibold text-white transition-transform active:scale-[0.99]"
                  style={{ backgroundColor: '#1e3a8a' }}
                >
                  {o.cta}
                  <ArrowRight size={17} strokeWidth={2.2} />
                </button>
              ) : (
                <div className="text-[12.5px]" style={{ color: '#64748b' }}>{o.pied}</div>
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  </>
);

export default PreuveTarif;
